<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import { Modal } from "@/components";
import { useDeleteSharedSpaceTemplateMutation } from "@/graphql/operations";
import { useFlashMessage } from "@/hooks";
import { useI18n } from "vue-i18n";
import type { TemplateListItem } from "./templateTypes";

const props = defineProps<{
  template: TemplateListItem;
  templates: TemplateListItem[];
}>();

const emit = defineEmits(["close", "deleted"]);

const { t } = useI18n();
const { displayFlashMessage } = useFlashMessage();
const { executeMutation: deleteTemplate } =
  useDeleteSharedSpaceTemplateMutation();

const replacementConfigId = ref("");
const isSubmitting = ref(false);
const replacementRequiredSpaceCount = ref<number | null>(null);

const templateName = computed(
  () => props.template.name || t("templates.unnamed"),
);
const displayedSpaceCount = computed(
  () => replacementRequiredSpaceCount.value ?? props.template.spaceCount,
);
const needsReplacement = computed(() => displayedSpaceCount.value > 0);
const compatibleReplacements = computed(() =>
  props.templates
    .filter(
      (template) =>
        template.id !== props.template.id &&
        template.teamId === props.template.teamId &&
        template.groupId === props.template.groupId,
    )
    .map((template) => ({
      value: template.id,
      title: template.name || t("templates.unnamed"),
    }))
    .sort((left, right) => left.title.localeCompare(right.title)),
);
const hasCompatibleReplacement = computed(
  () => compatibleReplacements.value.length > 0,
);
const isBlocked = computed(
  () => needsReplacement.value && !hasCompatibleReplacement.value,
);
const canSubmit = computed(
  () =>
    !isSubmitting.value &&
    !isBlocked.value &&
    (!needsReplacement.value || !!replacementConfigId.value),
);
const submitLabel = computed(() =>
  needsReplacement.value
    ? t("templates.deleteModal.submitWithReplacement")
    : t("templates.deleteModal.submit"),
);

watch(
  compatibleReplacements,
  (items) => {
    if (!items.some((item) => item.value === replacementConfigId.value)) {
      replacementConfigId.value = items[0]?.value ?? "";
    }
  },
  { immediate: true },
);

const submit = async () => {
  if (!canSubmit.value) {
    return;
  }

  isSubmitting.value = true;
  const result = await deleteTemplate({
    id: props.template.id,
    replacementConfigId: needsReplacement.value
      ? replacementConfigId.value
      : null,
  });

  if (result.error) {
    displayFlashMessage(t("templates.deleteModal.error"), "error");
    isSubmitting.value = false;
    return;
  }

  const deletion = result.data?.deleteSharedSpaceTemplate?.result;

  if (deletion?.status === "replacement_required") {
    replacementRequiredSpaceCount.value =
      deletion.affectedSpaceCount && deletion.affectedSpaceCount > 0
        ? deletion.affectedSpaceCount
        : Math.max(displayedSpaceCount.value, 1);
    displayFlashMessage(
      t("templates.deleteModal.replacementRequiredRace"),
      "error",
    );
    isSubmitting.value = false;
    return;
  }

  if (deletion?.status === "deleted") {
    displayFlashMessage(t("templates.deleteModal.success"), "success");
    emit("deleted");
    isSubmitting.value = false;
    return;
  }

  displayFlashMessage(t("templates.deleteModal.error"), "error");
  isSubmitting.value = false;
};
</script>

<template>
  <Modal :active="true" @close="emit('close')">
    <template #header>
      {{ $t("templates.deleteModal.title") }}
    </template>

    <template #body>
      <p class="intro">
        {{
          $t("templates.deleteModal.message", {
            name: templateName,
          })
        }}
      </p>

      <div class="template_summary">
        <div class="summary_item">
          <span class="summary_label">
            {{ $t("templates.deleteModal.details.name") }}
          </span>
          <span class="summary_value">{{ templateName }}</span>
        </div>
        <div class="summary_item">
          <span class="summary_label">
            {{ $t("templates.deleteModal.details.team") }}
          </span>
          <span class="summary_value">{{ template.teamName }}</span>
        </div>
        <div class="summary_item">
          <span class="summary_label">
            {{ $t("templates.deleteModal.details.group") }}
          </span>
          <span class="summary_value">{{ template.groupName }}</span>
        </div>
        <div class="summary_item">
          <span class="summary_label">
            {{ $t("templates.deleteModal.details.spaces") }}
          </span>
          <span class="summary_value">{{ displayedSpaceCount }}</span>
        </div>
      </div>

      <div v-if="needsReplacement" class="replacement_section">
        <p class="replacement_notice">
          {{
            $t("templates.deleteModal.usedBySpaces", {
              count: displayedSpaceCount,
            })
          }}
        </p>

        <div v-if="hasCompatibleReplacement" class="row">
          <label class="select_label" for="replacement-template">
            {{ $t("templates.deleteModal.replacement.label") }}
          </label>
          <select id="replacement-template" v-model="replacementConfigId">
            <option
              v-for="replacement in compatibleReplacements"
              :key="replacement.value"
              :value="replacement.value"
            >
              {{ replacement.title }}
            </option>
          </select>
          <p class="help">
            {{ $t("templates.deleteModal.replacement.help") }}
          </p>
        </div>

        <p v-else class="warning">
          {{ $t("templates.deleteModal.noReplacement") }}
        </p>
      </div>

      <p v-else class="help">
        {{ $t("templates.deleteModal.unused") }}
      </p>
    </template>

    <template #footer>
      <div class="buttons">
        <button class="button secondary" @click="emit('close')">
          {{ $t("cancel") }}
        </button>
        <button
          class="button dangerous"
          :class="{ disabled: !canSubmit }"
          :disabled="!canSubmit"
          @click="submit"
        >
          {{ submitLabel }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<style scoped lang="scss">
.intro {
  margin-top: 0;
}

.template_summary {
  border: 1px solid var(--classic-border-subtle, #e3e3e3);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 0.7em;
  margin: 1em 0;
  padding: 0.75em 1em;
}

.summary_item {
  align-items: flex-start;
  display: flex;
  gap: 1em;
  justify-content: space-between;
}

.summary_label {
  color: var(--classic-text-secondary, #666);
  flex: 1 1 auto;
}

.summary_value {
  color: var(--classic-text, #222);
  flex: 0 1 55%;
  font-weight: 600;
  text-align: right;
  word-break: break-word;
}

.replacement_section {
  margin-top: 1em;
}

.replacement_notice {
  color: var(--classic-text, #222);
  margin: 0 0 1em;
}

.row {
  display: flex;
  flex-direction: column;
  gap: 0.5em;
}

.select_label {
  color: var(--classic-text, #222);
  display: block;
  font-size: 0.9em;
  font-weight: 600;
}

select {
  appearance: auto;
  background: var(--classic-surface, #fff);
  border: 1px solid var(--classic-border, #d0d4d9);
  border-radius: 4px;
  color: var(--classic-text, #222);
  min-height: 2.5em;
  padding: 0.45em 0.6em;
  width: 100%;
}

.help {
  color: var(--classic-text-secondary, #666);
  font-size: 0.85em;
  margin: 0.5em 0 0;
}

.warning {
  background: var(--classic-warning-surface, #fff7ed);
  border: 1px solid var(--classic-warning, #f1c28d);
  border-radius: 8px;
  color: var(--classic-warning, #8a4a00);
  margin-bottom: 0;
  padding: 0.85em 1em;
}
</style>
